import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Paper,
  Stack,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Container,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { motion } from "framer-motion";
import LandscapeIcon from "@mui/icons-material/Landscape";
import TerrainIcon from "@mui/icons-material/Terrain";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import ForestIcon from "@mui/icons-material/Forest";
import { useNavigate } from "react-router-dom";
import TourCard from "../Components/Cards/TourCard";


const HimachalPradeshPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tourPackages = [
    {
      id: "hp1",
      title: "Complete Himachal Explorer",
      places: "Shimla • Manali • Kasol • Dharamshala • Dalhousie • Spiti • Kinnaur",
      price: "₹ 32,999/-",
      image:
        "https://sandpebblestours.com/wp-content/uploads/2018/09/Himalaya_Tour_Blog.jpg",
      rating: 4.9,
    },
    {
      id: "hp2",
      title: "Spiti Valley Ultimate Expedition",
      places: "Manali • Kaza • Key Monastery • Kibber • Langza • Chandratal",
      price: "₹ 41,499/-",
      image:
        "https://nomadictrails.in/wp-content/uploads/2023/08/wanderon-spiti-1-1.webp",
      rating: 4.9,
    },
    {
      id: "hp3",
      title: "Shimla – Manali Classic Tour",
      places: "Shimla • Kufri • Manali • Solang Valley • Atal Tunnel",
      price: "₹ 14,499/-",
      image:
        "https://maashaktitours.com/wp-content/uploads/2019/06/mera-himachal.jpg",
      rating: 4.7,
    },
    {
      id: "hp4",
      title: "Kasol – Manali Backpacker Tour",
      places: "Kasol • Chalal • Tosh • Manali • Old Manali",
      price: "₹ 12,999/-",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQR874n4sCmSchNFQrUZki_hFlUXsTPgiHkow&s",
      rating: 4.6,
    },
  ];

  const enrichTour = (tour, index) => {
    const durations = ["14D / 13N", "8D / 7N", "6D / 5N", "7D / 6N"];
    const tags = ["Best Seller", "Top Adventure", "Popular", "Trending"];

    return {
      ...tour,
      duration: durations[index % durations.length],
      tag: tags[index % tags.length],
      oldPrice:
        "₹ " +
        (parseInt(tour.price.replace(/[₹,/-]/g, "")) + 5000).toLocaleString() +
        "/-",
      discountEndsIn: "Limited Time Offer",
    };
  };

  return (
    <Box sx={{ backgroundColor: "#F9FAFB", minHeight: "100vh" }}>
      
      {/* HERO SECTION */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "60vh", md: "75vh" },
          backgroundImage:
            "url(https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?q=80&w=1920&auto=format&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, textAlign: "center", color: "white" }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                fontSize: { xs: "2.5rem", md: "4.5rem" },
                mb: 2,
                textShadow: "0 4px 12px rgba(0,0,0,0.5)",
              }}
            >
              Himachal Pradesh
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 300, letterSpacing: 2, mb: 4, opacity: 0.9 }}
            >
              THE LAND OF GODS
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => document.getElementById("packages").scrollIntoView({ behavior: "smooth" })}
              sx={{
                backgroundColor: "#D97706",
                color: "white",
                px: 4,
                py: 1.5,
                borderRadius: 50,
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0 4px 14px rgba(217, 119, 6, 0.4)",
                "&:hover": { backgroundColor: "#B45309", transform: "translateY(-2px)" },
              }}
            >
              Explore Packages
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* INTRO SECTION */}


      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Typography variant="overline" sx={{ color: "#059669", fontWeight: 700, letterSpacing: 1.5 }}>
            DEV BHOOMI
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mt: 1,
              mb: 3,
              color: "#1F2937"
            }}
          >
            Experience the Magic of the Himalayas
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#6B7280", lineHeight: 1.8, fontSize: "1.1rem" }}
          >
            Himachal Pradesh is a land of changing landscapes — from colonial hill stations and pine forests to raw Himalayan deserts and ancient monasteries. Whether you want relaxed hill towns, café culture, trekking trails, or extreme high-altitude adventures like Spiti Valley, Himachal offers something for every kind of traveler.
          </Typography>
        </motion.div>
      </Container>

      {/* PACKAGES SECTION */}
      <Box id="packages" sx={{ py: 6, backgroundColor: "#fff" }}>
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 5,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Curated Tour Packages
          </Typography>
          
          <Grid container spacing={4}>
            {tourPackages.map((tour, index) => (
              <Grid item xs={12} sm={6} md={3} key={tour.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <TourCard
                    tour={tour}
                    index={index}
                    onViewDetails={() =>
                      navigate("/tourdetails", {
                        state: enrichTour(tour, index),
                      })
                    }
                    onEnquire={() => navigate("/booking")}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* PLACES SECTION (ZIG-ZAG LAYOUT) */}
      <Box sx={{ py: 10, backgroundColor: "#F9FAFB" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 8,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Places You Will Explore
          </Typography>

          {[
            {
              title: "Manali",
              img: "https://www.kullumanalitrips.com/images/manali-tour-package-images/manali-tour-package-1383.jpg",
              desc: "Manali is the adventure capital of Himachal, known for rivers, cafés, mountain passes, and snow activities.",
            },
            {
              title: "Spiti Valley",
              img: "https://nomadictrails.in/wp-content/uploads/2023/08/wanderon-spiti-1-1.webp",
              desc: "Spiti is a high-altitude cold desert with ancient monasteries, dramatic landscapes, and raw Himalayan life.",
            },
            {
              title: "Kasol",
              img: "https://static.justwravel.com/images/cgnfe1hd/production/d0b664e6209d68f8c808884b5a85e76eaa35eee2-938x850.webp?fm=webp",
              desc: "Kasol is a backpacker hub famous for café culture, Parvati river walks, and village trails.",
            },
            {
              title: "Dharamshala",
              img: "https://www.indiantempletour.com/wp-content/uploads/2022/08/Dharamshala-Tour-Package.jpg",
              desc: "Dharamshala & McLeod Ganj are known for Tibetan culture, monasteries, and peaceful mountain views.",
            },
          ].map((place, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Grid
                container
                spacing={6}
                alignItems="center"
                sx={{
                  mb: 10,
                  flexDirection: { xs: "column", md: i % 2 === 0 ? "row" : "row-reverse" },
                }}
              >
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      height: { xs: "300px", md: "400px" },
                    }}
                  >
                    <CardMedia component="img" image={place.img} alt={place.title} sx={{ height: "100%", objectFit: "cover" }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="overline"
                    sx={{ color: "#059669", fontWeight: "bold" }}
                  >
                    DESTINATION
                  </Typography>
                  <Typography variant="h3" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, my: 2 }}>
                    {place.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#4B5563", lineHeight: 1.8 }}>
                    {place.desc}
                  </Typography>
                  </Grid>
              </Grid>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* EXPERIENCES SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Experiences That Define Himachal
          </Typography>
          <Typography sx={{ textAlign: "center", color: "#6B7280", mb: 6 }}>
            Discover the soul of the mountains.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <TerrainIcon />, text: "High-altitude adventures & road trips" },
              { icon: <LandscapeIcon />, text: "Mountain views & scenic valleys" },
              { icon: <TempleHinduIcon />, text: "Temples, monasteries & spirituality" },
              { icon: <DirectionsWalkIcon />, text: "Trekking & village walks" },
              { icon: <AcUnitIcon />, text: "Snowfall & winter experiences" },
              { icon: <ForestIcon />, text: "Forests, rivers & slow travel" },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: "100%",
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "0.3s",
                      "&:hover": {
                        borderColor: "#059669",
                        boxShadow: "0 10px 30px -10px rgba(6, 78, 59, 0.15)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 40,
                        color: "#059669",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 60,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography fontWeight={600} sx={{ color: "#374151" }}>
                      {item.text}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TABLE SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#F3F4F6" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ p: 4, borderBottom: "1px solid #E5E7EB" }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
                Himachal Pradesh – Travel Overview
              </Typography>
            </Box>
            <Table>
              <TableHead sx={{ backgroundColor: "#064E3B" }}>
                <TableRow>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Place</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Altitude</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Region</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Known For</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["Manali", "2,050 m", "Kullu", "Adventure & cafés"],
                  ["Spiti", "4,200 m", "Lahaul-Spiti", "Cold desert & monasteries"],
                  ["Kasol", "1,580 m", "Parvati Valley", "Backpacker culture"],
                  ["Dharamshala", "1,457 m", "Kangra", "Tibetan culture"],
                  ["Shimla", "2,276 m", "Shimla", "Colonial hill station"],
                ].map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 }, "&:hover": { backgroundColor: "#F9FAFB" } }}
                  >
                    {row.map((cell, j) => (
                      <TableCell key={j} sx={{ color: "#4B5563" }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Container>
      </Box>

      {/* CTA SECTION */}
      <Box
        sx={{
          py: 12,
          backgroundImage: "linear-gradient(135deg, #065F46 0%, #047857 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: "2rem", md: "3rem" }
              }}
            >
              Ready to Explore Himachal?
            </Typography>
            <Typography sx={{ maxWidth: "800px", mx: "auto", lineHeight: 1.8, fontSize: "1.1rem", opacity: 0.9, mb: 5 }}>
              From relaxed hill stations to the extreme beauty of Spiti Valley, Himachal offers journeys that stay with you for life.
            </Typography>
            <Button
              size="large"
              variant="contained"
              sx={{
                backgroundColor: "#D97706",
                color: "white",
                fontWeight: 700,
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                borderRadius: 50,
                boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: "#B45309" },
              }}
              onClick={() => navigate("/booking")}
            >
              Enquire Now
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default HimachalPradeshPage;